/**
 * PULSE — entries per bucket, the comparison window behind it, incident days
 * marked on the line, a brush that refilters the screen, and drag-to-zoom.
 *
 * ── ONE Y-AXIS, AND THIS IS THE PANEL WHERE THAT MATTERS MOST ──
 *
 * Incidents are a SUBSET of entries: every incident is already one of the
 * entries plotted on this line. Giving them a second, auto-scaled axis would
 * stretch three incidents to the full height of a chart whose other series peaks
 * at four hundred, and the two lines would appear to cross — a reader would come
 * away believing incidents had overtaken entries. So the incident days ride the
 * entries scale as MARKERS positioned at that day's entry count, and the count
 * itself is in the tooltip.
 *
 * `dimension=day` is NOT zero-filled by the server, so a day with nothing
 * recorded is absent rather than zero, and the server says so in `notes`. That
 * is why the axis is categorical: the chart shows the days that exist and does
 * not draw a valley through days it has no record of.
 */
import React, { useCallback, useMemo } from 'react';
import {
  Bar,
  Brush,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceArea,
  ReferenceDot,
  Scatter,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  AnalyticsBatchOk,
  AnalyticsBatchResult,
  isBatchFailure,
} from '../../../../../api/analytics-client';
import { dataViz, palette } from '../../../../../style-utils/theme';
import { ChartShell, ChartShellState } from '../charts/chart-shell';
import { ChartTable, describeSeries } from '../charts/chart-table-view';
import { DashLegend, hiddenKeys } from '../charts/chart-legend';
import { DashTooltip } from '../charts/chart-tooltip';
import {
  ONE_BUCKET_NOTE,
  SYNC_ID,
  activeDot,
  compareSeries,
  cursor,
  firstPaintAnimation,
  format,
  grid,
  lineMark,
  margins,
  oneBucket,
  oneBucketBar,
  oneBucketCompareBar,
  referenceLabel,
  referenceLine,
  soloPoint,
  xAxisTime,
  yAxisCount,
} from '../charts/chart-theme';
import { Anomaly, anomalyIndex, findAnomalies } from '../charts/anomaly';
import { readSlot, slotState, substitutionCaveat } from './panel-utils';
import { DrillHandlers } from '../charts/use-chart-drill';
import { PanelToggle } from '../charts/chart.styled';
import { ZoomHandlers } from '../charts/use-chart-zoom';
import { useBrushAria } from '../charts/brush-aria';
import { useCompareMeaning } from '../charts/compare-label';

const PANEL_ID = 'pulse';

interface PulsePoint {
  key: string;
  label: string;
  value: number;
  compare: number | null;
  incidents: number;
  /** The entries value on a day that had at least one incident, null otherwise.
   *  Null keeps the Scatter from drawing a mark on a clean day. */
  incidentAt: number | null;
}

/** >=8px across, with a 2px surface ring, because this mark lands ON the line it
 *  annotates and without the ring the two read as one blob. */
const IncidentDot: React.FC<{ cx?: number; cy?: number }> = ({ cx, cy }) => {
  if (cx == null || cy == null) return null;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill={palette.alert}
      stroke={palette.surface}
      strokeWidth={2}
    />
  );
};

const pointsFrom = (
  entries: AnalyticsBatchOk,
  incidents?: AnalyticsBatchResult
): PulsePoint[] => {
  const byDay = new Map<string, number>();
  if (incidents && !isBatchFailure(incidents)) {
    for (const item of incidents.items) {
      byDay.set(item.key, format.numberOf(item.value) ?? 0);
    }
  }

  return entries.items.map(item => {
    const value = format.numberOf(item.value) ?? 0;
    const incidentCount = byDay.get(item.key) ?? 0;

    return {
      key: item.key,
      label: format.dayKey(item.key),
      value,
      compare: item.compareValue,
      incidents: incidentCount,
      incidentAt: incidentCount > 0 ? value : null,
    };
  });
};

export interface PulsePanelProps {
  state: ChartShellState;
  entries?: AnalyticsBatchResult;
  incidents?: AnalyticsBatchResult;
  comparing: boolean;
  /** The local clock time the period ENDS at, when that is not midnight. */
  endsAt?: string | null;
  seriesOff: string[];
  onToggleSeries: (key: string) => void;
  drill: DrillHandlers;
  zoom: ZoomHandlers;
  showBrush: boolean;
  onExportPng?: (svg: SVGSVGElement | null) => void;
  onExportCsv?: () => void;
}

export const PulsePanel: React.FC<PulsePanelProps> = ({
  state,
  entries,
  incidents,
  comparing,
  endsAt,
  seriesOff,
  onToggleSeries,
  drill,
  zoom,
  showBrush,
  onExportPng,
  onExportCsv,
}) => {
  const extra: string[] = [];

  /* THE MARK CHANGES WHEN THE PERIOD HOLDS ONE BUCKET — see chart-theme's
     one-bucket section. Counted off the RESPONSE rather than off `points`
     because the caveat list is assembled before the points are, and a note that
     arrives one render later is a note the reader has already scrolled past. */
  const solo =
    !!entries && !isBatchFailure(entries) && oneBucket(entries.items.length);

  if (solo) extra.push(ONE_BUCKET_NOTE);

  // THE PLUNGING LAST POINT.
  //
  // A rolling range ends at the moment of the request, so its final bucket is a
  // PART of a day — and monotone interpolation draws that as a cliff. The first
  // browser pass on the analytics screen showed a line running at ~150 a day for
  // a week and then dropping to 20, which reads as a collapse and is nothing but
  // the clock. The data is right; the shape is the lie, and the fix is to say so
  // where the shape is.
  if (endsAt) {
    extra.push(
      `The last bucket on this chart is still in progress — the period ends at ${endsAt}, ` +
        'not at midnight — so its final point covers part of a day and the drop into it ' +
        'is the clock rather than a fall in activity.'
    );
  }

  // A failed incidents panel must not take the pulse down with it, and must not
  // pass silently either: an unmarked line would read as "no incidents".
  if (incidents && isBatchFailure(incidents)) {
    extra.push(
      'Incident days could not be marked on this chart: ' +
        incidents.error +
        '. An unmarked day here is therefore NOT evidence that nothing happened.'
    );
  }

  const compareMeaning = useCompareMeaning();

  const slot = readSlot(entries, extra);
  const points = useMemo(
    () => (slot.ok ? pointsFrom(slot.ok, incidents) : []),
    [slot.ok, incidents]
  );

  const anomalies = useMemo<Anomaly[]>(
    () =>
      findAnomalies(
        points.map(p => ({ label: p.label, value: p.value })),
        // The final bucket of a rolling range is a fraction of a day and is
        // ALWAYS low; flagging it would mark the clock as an anomaly every load.
        { ignoreLast: Boolean(endsAt) }
      ),
    [points, endsAt]
  );
  const anomalyAt = useMemo(() => anomalyIndex(anomalies), [anomalies]);

  /* THE BRUSH IS A SLIDER AND ANNOUNCED ITSELF AS "undefined". See
     `charts/brush-aria.ts` — the library composes the handle's name from a
     `.name` field these rows do not have, and puts an SVG x-coordinate in
     `aria-valuenow`. This supplies the name, the range and the date. */
  const brushLabels = useMemo(() => points.map(p => p.label), [points]);
  const onBrushRange = useCallback(
    (startIndex: number, endIndex: number) => {
      const from = points[startIndex]?.key;
      const to = points[endIndex]?.key;
      if (from && to) zoom.onBrush(from, to);
    },
    /* `zoom` itself is rebuilt every render; `zoom.onBrush` is not. Depending on
       the object would rebind the brush's key listener on every keystroke
       anywhere on the screen. */
    [points, zoom.onBrush]
  );
  const brush = useBrushAria({
    panelId: PANEL_ID,
    labels: brushLabels,
    onRange: onBrushRange,
  });

  const hidden = hiddenKeys(seriesOff, PANEL_ID);
  const hasCompare = points.some(p => p.compare != null);
  const hasIncidents = points.some(p => p.incidents > 0);
  /* THE MEAN OF ONE BUCKET IS THAT BUCKET. Drawn anyway it lands exactly on the
     reading, so the panel shows a line labelled `mean 91` sitting on top of the
     only bar — which is how the first report came to describe a chart that has
     one figure in it as one that "displays nothing". Two buckets is the least
     that makes an average a second fact. */
  const mean =
    points.length > 1
      ? points.reduce((sum, p) => sum + p.value, 0) / points.length
      : 0;

  const legend = [
    { key: 'value', label: 'Log entries', tone: dataViz.series[0] },
    ...(hasCompare
      ? [
          {
            key: 'compare',
            label: compareMeaning.label,
            tone: dataViz.axis,
            dashed: true,
          },
        ]
      : []),
    ...(hasIncidents
      ? [{ key: 'incidentAt', label: 'Day with an incident', tone: palette.alert }]
      : []),
  ];

  return (
    <ChartShell
      id={PANEL_ID}
      title="Pulse"
      wide
      aspect={2.6}
      minHeight={280}
      state={slotState(state, slot)}
      notes={slot.notes}
      caveat="Log entries per bucket, with the previous period behind it. Days with an incident are marked on the line. Drag across the chart to zoom, or use the handles below it to narrow the whole screen."
      extraCaveat={substitutionCaveat(slot.ok)}
      emptyReason={
        slot.empty
          ? {
              title: 'Nothing was recorded in this period',
              text: 'A quiet log and a quiet building are different things. Widen the range or pick another building.',
            }
          : null
      }
      summary={describeSeries('Log entries', points, ['entry', 'entries'])}
      onExportPng={onExportPng}
      onExportCsv={onExportCsv}
      table={
        slot.ok ? (
          <ChartTable
            dimensionLabel="Bucket"
            valueLabel="Log entries"
            showCompare={comparing}
            rows={points.map(p => ({
              key: p.key,
              label: p.label,
              value: p.value.toLocaleString(),
              compareValue: p.compare == null ? '—' : p.compare.toLocaleString(),
              delta:
                p.compare == null ? '—' : format.delta(p.value - p.compare),
            }))}
          />
        ) : undefined
      }
      controls={
        <>
          {/* Same gesture as the Overview chart, same missing way back - see
              the note on the traffic panel. */}
          {zoom.zoomed ? (
            <PanelToggle type="button" data-testid="reset-zoom" onClick={zoom.reset}>
              Reset zoom
            </PanelToggle>
          ) : null}
          <DashLegend
            panelId={PANEL_ID}
            entries={legend}
            seriesOff={seriesOff}
            onToggle={onToggleSeries}
          />
        </>
      }
    >
      <ComposedChart accessibilityLayer
        data={points}
        margin={showBrush ? margins.withBrush : margins.default}
        /* ONE CATEGORY OWNS THE WHOLE PLOT WIDTH, so recharts spreads a group of
           two bars from edge to edge and the pair stops reading as a pair. These
           two props pull them back into the middle. They are set ONLY in the
           one-bucket case: this panel draws no bars otherwise, and a category
           gap left armed would be a control waiting to surprise the next series
           somebody adds. */
        barCategoryGap={solo ? '38%' : undefined}
        barGap={solo ? 10 : undefined}
        syncId={SYNC_ID}
        onMouseDown={zoom.onMouseDown}
        onMouseMove={zoom.onMouseMove}
        onMouseUp={zoom.onMouseUp}
      >
        <CartesianGrid {...grid} />
        <XAxis dataKey="label" {...xAxisTime} />
        {/* ONE axis. See the header of this file. */}
        <YAxis {...yAxisCount} />
        <Tooltip
          content={
            <DashTooltip
              series={[
                {
                  key: 'value',
                  label: 'log entries',
                  tone: dataViz.series[0],
                  noun: ['log entry', 'log entries'],
                },
                {
                  key: 'compare',
                  label: compareMeaning.inline,
                  tone: dataViz.axis,
                  dashed: true,
                  isCompare: true,
                },
                {
                  key: 'incidents',
                  label: 'incidents',
                  tone: palette.alert,
                  noun: ['incident', 'incidents'],
                },
              ]}
              hidden={hidden}
              /* The fixed note that keeps the shared scale honest, PLUS the
                 anomaly's own reason when the hovered point has one. */
              note={row => {
                const anomaly = anomalyAt.get(String(row.label));

                return (
                  <>
                    Incidents are a subset of these entries, so they share this
                    scale.
                    {anomaly ? (
                      <>
                        <br />
                        <strong>Unusual:</strong> {anomaly.reason}
                      </>
                    ) : null}
                  </>
                );
              }}
            />
          }
          cursor={cursor.line}
        />

        {/* THE PERIOD MEAN. A line a reader can measure a spike against without
            doing arithmetic. */}
        {mean > 0 ? (
          <ReferenceArea
            y1={mean}
            y2={mean}
            {...referenceLine}
            fill="none"
            label={{
              ...referenceLabel,
              value: `mean ${Math.round(mean).toLocaleString()}`,
              position: 'insideTopRight',
            }}
          />
        ) : null}

        {/* DRAG-TO-ZOOM, painted while the pointer is down. */}
        {zoom.selection ? (
          <ReferenceArea
            x1={zoom.selection.from}
            x2={zoom.selection.to}
            fill={palette.iris}
            fillOpacity={0.12}
            stroke={palette.iris}
          />
        ) : null}

        {/* ── THE COMPARISON READING ──
            A bar beside the period's own bar when there is one bucket, the
            muted dashed line otherwise. Rendered BEFORE the primary so the pair
            reads left-to-right as earlier-then-now. */}
        {hasCompare && !hidden.includes('compare') ? (
          solo ? (
            <Bar
              dataKey="compare"
              name={compareMeaning.label}
              {...oneBucketCompareBar}
            />
          ) : (
            <Line
              dataKey="compare"
              name={compareMeaning.label}
              {...compareSeries}
              {...soloPoint(points.length, dataViz.axis)}
            />
          )
        ) : null}

        {/* ── THE READING ──
            ONE BUCKET IS A QUANTITY, NOT A SHAPE, so it is a bar. See the
            one-bucket section of chart-theme for why the previous round's
            bigger dot was not enough. */}
        {!hidden.includes('value') ? (
          solo ? (
            <Bar
              dataKey="value"
              name="Log entries"
              fill={dataViz.series[0]}
              {...oneBucketBar}
              // The bar is a drill target for the same reason every point is.
              onClick={drill.onMark}
              cursor="pointer"
            />
          ) : (
            <Line
              dataKey="value"
              name="Log entries"
              {...lineMark}
              {...soloPoint(points.length, dataViz.series[0])}
              stroke={dataViz.series[0]}
              activeDot={{
                ...activeDot(dataViz.series[0]),
                // EVERY POINT IS A DRILL TARGET. Clicking one narrows the screen
                // to that bucket and jumps to the rows behind it.
                onClick: drill.onMark,
                cursor: 'pointer',
              }}
              {...firstPaintAnimation(PANEL_ID)}
            />
          )
        ) : null}

        {/* On a one-bucket period this marker rides the middle of the band while
            the two bars sit either side of it, so it reads as a flag ABOVE the
            pair rather than as a mark on one of them. That is the right meaning
            — the flag is about the DAY — and the count is in the tooltip. */}
        {hasIncidents && !hidden.includes('incidentAt') ? (
          <Scatter
            dataKey="incidentAt"
            name="Day with an incident"
            shape={<IncidentDot />}
            isAnimationActive={false}
            onClick={drill.onMark}
            cursor="pointer"
          />
        ) : null}

        {/* ANOMALY MARKERS. A point more than 2σ off its own trailing baseline,
            with a plain-language reason on hover — never a bare sigma. */}
        {anomalies.map(anomaly => (
          <ReferenceDot
            key={anomaly.label}
            x={anomaly.label}
            y={anomaly.value}
            r={7}
            fill="none"
            stroke={palette.warning}
            strokeWidth={2}
            isFront
            data-testid="anomaly-marker"
          />
        ))}

        {showBrush && points.length > 6 ? (
          <Brush
            dataKey="label"
            height={22}
            travellerWidth={12}
            stroke={palette.lineStrong}
            fill={palette.surfaceMuted}
            // The generic name both handles share until the effect writes the
            // per-handle ones. Without it recharts composes the name from a
            // field these rows do not have and both handles announce
            // "Min value: undefined, Max value: undefined".
            ariaLabel={brush.ariaLabel}
            // Wired back into the GLOBAL date range, so brushing one chart
            // refilters the whole screen. recharts 2.x does NOT synchronise a
            // Brush across syncId, which is why this is lifted rather than left
            // to the library.
            onChange={brush.onChange}
          />
        ) : null}
      </ComposedChart>
    </ChartShell>
  );
};
